import React, { useMemo, useState } from 'react';
import { Row, Col, Select, Switch, Modal, InputNumber, Input, Divider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { Button, Toast } from '../../ui';
import useBudgetConfig from '../../../hooks/useBudgetConfig';
import BudgetTable from './BudgetTable';

const BudgetConfig = ({ syncId, syncName, onBack }) => {
  const { theme } = useTheme();
  const {
    loading,
    year,
    setYear,
    monthNames,
    categories,
    showVM, setShowVM,
    showVP, setShowVP,
    showVO, setShowVO,
    isLeaf,
    setValue,
    getAggregatedValue,
    fillFromAPI,
    save,
    recalculateVMByNature,
    recalculateVOByNature,
    recalculateVPByNature
  } = useBudgetConfig(syncId);

  // Fill dialog
  const [fillVisible, setFillVisible] = useState(false);
  const [fillMode, setFillMode] = useState('complete'); // 'complete' | 'overwrite'
  const [fillLoading, setFillLoading] = useState(false);
  // Calc dialog
  const [calcVisible, setCalcVisible] = useState(false);

  const [vmPercentReceitas, setVmPercentReceitas] = useState(0);
  const [vmPercentDespesas, setVmPercentDespesas] = useState(0);
  const [voPercentReceitas, setVoPercentReceitas] = useState(10);
  const [voPercentDespesas, setVoPercentDespesas] = useState(10);
  const [vpPercentReceitas, setVpPercentReceitas] = useState(10);
  const [vpPercentDespesas, setVpPercentDespesas] = useState(10);

  const [hiddenMonths, setHiddenMonths] = useState(() => new Set());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showZeroRows, setShowZeroRows] = useState(false);

  const toggleMonth = (m) => {
    setHiddenMonths(prev => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m); else next.add(m);
      return next;
    });
  };
  const resetMonths = () => setHiddenMonths(new Set());

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const yearOptions = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y <= currentYear + 5; y++) {
      arr.push({ label: String(y), value: y });
    }
    return arr;
  }, [currentYear]);

  const groupedCategories = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    const labels = { R: 'Receitas', P: 'Despesas' };
    const groupsMap = new Map();

    categories.forEach((cat) => {
      const natureza = cat.natureza || cat.Natureza || 'outras';
      if (!groupsMap.has(natureza)) {
        groupsMap.set(natureza, {
          id: `nature-${natureza}`,
          name: labels[natureza] || `Natureza ${natureza}`,
          __virtual: true,
          natureza,
          children: [],
        });
      }
      groupsMap.get(natureza).children.push(cat);
    });

    return Array.from(groupsMap.values());
  }, [categories]);

  const filteredCategories = useMemo(() => {
    const base = groupedCategories.length ? groupedCategories : categories;
    if (!categoryFilter.trim()) return base;

    const filterText = categoryFilter.trim().toLowerCase();

    const filterTree = (nodes) => {
      if (!Array.isArray(nodes)) return [];
      return nodes
        .map((node) => {
          const name = String(node.name || '').toLowerCase();
          const children = filterTree(node.children || []);
          const selfMatch = name.includes(filterText);
          if (selfMatch || children.length > 0) {
            return {
              ...node,
              children: children.length > 0 ? children : undefined,
            };
          }
          return null;
        })
        .filter(Boolean);
    };

    return filterTree(base);
  }, [groupedCategories, categories, categoryFilter]);

  const [showCenario, setShowCenario] = useState(['medio', 'pessimista', 'otimista']);
  const handleShowCenario = (value) => {
    console.log(value);
    setShowCenario(value);
    if (value.includes('medio')) {
      setShowVM(true);
    } else {
      setShowVM(false);
    }
    if (value.includes('pessimista')) {
      setShowVP(true);
    } else {
      setShowVP(false);
    }
    if (value.includes('otimista')) {
      setShowVO(true);
    } else {
      setShowVO(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.background }}>
      <div style={{ width: '100%', margin: '0 auto' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{ padding: 8, color: theme.primary, marginBottom: 16 }}
        >
          Voltar para Configurações
        </Button>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24
        }}>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 600,
              color: theme.textPrimary
            }}>
              {`Configurando planejamento orçamentário para: ${syncName || ''}`}
            </h1>
            <p style={{
              margin: '4px 0 0',
              color: theme.textSecondary,
              fontSize: 14
            }}>
              Defina os valores de planejamento orçamentário por categoria e mês.
            </p>
          </div>
          <div>
            <Button type="primary" loading={loading} onClick={save}>
              Salvar Orçamento
            </Button>
          </div>
        </div>
      
        {/*Controle de configurações: */}
        <div style={{
          padding: 16,
          background: theme.cardBackground,
          borderRadius: 8,
          marginBottom: 8,
          border: `1px solid ${theme.border}`
        }}>
          <Row gutter={[16, 16] } align="middle">
            {/* Controle de ano do orçamento */}
            <Col xs={24} sm={12} md={6}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 8, color: theme.textPrimary }}>Ano do orçamento</div>
                <Select
                  style={{ width: '100%' }}
                  value={year}
                  onChange={setYear}
                  options={yearOptions}
                />
              </div>
            </Col>
            {/* Controle de ações de configurações */}
            <Col>
              {/* Div com margem para deixar no nívle do select do ano do orçamento */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 25 }}></div>
              <Row gutter={[16, 16]}>
                {/* Buscar valores realizados */}
                <Col>
                  <Button onClick={() => setFillVisible(true)} disabled={loading} size="small">
                    Buscar valores realizados
                  </Button>
                </Col>
                {/* Configurar cenários */}
                <Col>
                  <Button onClick={() => setCalcVisible(true)} disabled={loading} size="small">
                    Configurar cenários
                  </Button>
                </Col>
                {/* Mostrar todos os meses (Botão que fica oculto quando todos os meses estão visíveis) */}
                <Col>
                  {hiddenMonths.size > 0 && (
                    <Button onClick={resetMonths} disabled={loading} size="small">
                      Mostrar todos os meses
                    </Button>
                    )}
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
        {/* Controles de filtros e busca */}
        <div style={{
          padding: 16,
          background: theme.cardBackground,
          borderRadius: 8,
          marginBottom: 8,
          border: `1px solid ${theme.border}`
        }}>
           <Row gutter={[16, 16]} align="middle">
           {/* Campo de busca de categorias */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ fontWeight: 500, marginBottom: 8, color: theme.textPrimary }}>Filtrar categorias</div>
              <Input.Search
                placeholder="Digite para filtrar categorias"
                allowClear
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              />
            </Col>
            {/* Filtro de cenários */}
            <Col xs={24} sm={12} md={8}>
              <div style={{ fontWeight: 500, marginBottom: 8, color: theme.textPrimary }}>Filtro de cenários</div>
              <Select
                style={{ width: '100%' }}
                value={showCenario}
                onChange={handleShowCenario}
                mode="multiple"
                options={[{ label: 'Médio', value: 'medio' }, { label: 'Pessimista', value: 'pessimista' }, { label: 'Otimista', value: 'otimista' }]}
              />
            </Col>
            {/* Mostrar linhas zeradas */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ fontWeight: 500, marginBottom: 8, color: theme.textPrimary }}>Mostrar linhas zeradas</div>
              <Switch checked={showZeroRows} onChange={setShowZeroRows} />
            </Col>
          </Row>
        </div>
        {/* Divider */}
        <Divider size="small" style={{ borderColor: theme.border }} />
        {/* Tabela de orçamento */}
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <BudgetTable
              categories={filteredCategories}
              showVM={showVM}
              showVP={showVP}
              showVO={showVO}
              getAggregatedValue={getAggregatedValue}
              isLeaf={isLeaf}
              onChangeValue={setValue}
              monthNames={monthNames}
              hiddenMonths={hiddenMonths}
              onToggleMonth={toggleMonth}
              showZeroRows={showZeroRows}
            />
          </Col>
        </Row>
      </div>

      {/* Modal: Preencher VM da API */}
      <Modal
        title="Preencher valores do planejamento orçamentário realizado no ano anterior"
        open={fillVisible}
        onCancel={() => {
          if (!fillLoading) {
            setFillVisible(false);
          }
        }}
        okText="Preencher"
        confirmLoading={fillLoading}
        onOk={async () => {
          try {
            setFillLoading(true);
            await fillFromAPI(fillMode);
            setFillVisible(false);
          } catch (error) {
            console.error('Erro ao preencher valores:', error);
            // O erro já deve ser tratado pelo fillFromAPI ou hook
          } finally {
            setFillLoading(false);
          }
        }}
        cancelText="Cancelar"
        cancelButtonProps={{ disabled: fillLoading }}
        closable={!fillLoading}
      >
        <p style={{ color: theme.textSecondary }}>
          Como deseja aplicar os valores retornados do planejamento orçamentário realizado no ano anterior?
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textPrimary }}>
            <input
              type="radio"
              checked={fillMode === 'complete'}
              onChange={() => setFillMode('complete')}
            />
            Completar apenas os campos não alterados manualmente
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.textPrimary }}>
            <input
              type="radio"
              checked={fillMode === 'overwrite'}
              onChange={() => setFillMode('overwrite')}
            />
            Sobrescrever todos os valores realizados
          </label>
        </div>
      </Modal>

      {/* Modal: Calcular visões por natureza */}
      <Modal
        title="Calcular VM, VO e VP por natureza (Receitas/Despesas)"
        open={calcVisible}
        onCancel={() => setCalcVisible(false)}
        footer={<Button
          key="close"
          onClick={() => setCalcVisible(false)}
          style={{ marginTop: 2, justifyContent: 'flex-end' }}
          type="primary"
          disabled={loading}
        >
          Fechar
        </Button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* VM */}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Visão Média (VM)</div>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}>Entradas (Receitas) – %</div>
                <InputNumber
                  min={-1000}
                  max={1000}
                  value={vmPercentReceitas}
                  onChange={(v) => setVmPercentReceitas(typeof v === 'number' ? v : 0)}
                  controls
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}>Saídas (Despesas) – %</div>
                <InputNumber
                  min={-1000}
                  max={1000}
                  value={vmPercentDespesas}
                  onChange={(v) => setVmPercentDespesas(typeof v === 'number' ? v : 0)}
                  controls
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 4, color: theme.textSecondary, fontSize: 12 }}>
              Regra: para receitas, VM = VM × (1 + %). Para despesas, VM = VM × (1 − %).
              Percentuais positivos inflacionam (mais receita, menor despesa); negativos fazem o inverso.
            </div>
            <Row style={{ marginTop: 2, justifyContent: 'flex-end' }}>
              <Button
              key="vm"
              onClick={() => {
                recalculateVMByNature({
                  percReceitas: vmPercentReceitas,
                  percDespesas: vmPercentDespesas,
                });
                Toast.success('Visão média recalculada por natureza');
                }}
                disabled={loading}
              >
                Recalcular VM
              </Button>
            </Row>
          </div>

          {/* VO */}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Visão Otimista (VO)</div>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}>Entradas (Receitas) – %</div>
                <InputNumber
                  min={-1000}
                  max={1000}
                  value={voPercentReceitas}
                  onChange={(v) => setVoPercentReceitas(typeof v === 'number' ? v : 0)}
                  controls
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}>Saídas (Despesas) – %</div>
                <InputNumber
                  min={-1000}
                  max={1000}
                  value={voPercentDespesas}
                  onChange={(v) => setVoPercentDespesas(typeof v === 'number' ? v : 0)}
                  controls
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 4, color: theme.textSecondary, fontSize: 12 }}>
              Regra: para receitas, VO = VM × (1 + %). Para despesas, VO = VM × (1 − %).
              A diferença entre VO e VO está só nos percentuais que você informar
            </div>
            <Row style={{ marginTop: 2, justifyContent: 'flex-end' }}>
              <Button
              key="vo"
              onClick={() => {
                recalculateVOByNature({
                  percReceitas: voPercentReceitas,
                  percDespesas: voPercentDespesas,
                });
                Toast.success('Visão otimista recalculada por natureza');
                }}
                disabled={loading}
              >
                Recalcular VO
              </Button>
            </Row>
          </div>

          {/* VP */}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Visão Pessimista (VP)</div>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}>Entradas (Receitas) – %</div>
                <InputNumber
                  min={-1000}
                  max={1000}
                  value={vpPercentReceitas}
                  onChange={(v) => setVpPercentReceitas(typeof v === 'number' ? v : 0)}
                  controls
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}>Saídas (Despesas) – %</div>
                <InputNumber
                  min={-1000}
                  max={1000}
                  value={vpPercentDespesas}
                  onChange={(v) => setVpPercentDespesas(typeof v === 'number' ? v : 0)}
                  controls
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 4, color: theme.textSecondary, fontSize: 12 }}>
              Regra: para receitas, VP = VM × (1 + %). Para despesas, VP = VM × (1 − %).
              A diferença entre VO e VP está só nos percentuais que você informar.
            </div>
            <Row style={{ marginTop: 2, justifyContent: 'flex-end' }}>
              <Button
              key="vp"
              onClick={() => {
                recalculateVPByNature({
                  percReceitas: vpPercentReceitas,
                  percDespesas: vpPercentDespesas,
                });
                Toast.success('Visão pessimista recalculada por natureza');
                }}
                disabled={loading}
              >
                Recalcular VP
              </Button>
            </Row>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BudgetConfig;
